import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, map, switchMap, take } from 'rxjs';

import { EndType } from '@cpd-app/core-module/models';
import { TrackerService } from '@cpd-app/core-module/services';
import { VideoUsageInput } from '@cpd-app/generated';
import { STORAGE_KEY } from '@cpd-app/core-module/constants/storage-key.const';

import { AppState } from '..';
import { GoalFacade } from '../goal';
import { TrackerActions } from '.';
import { TrackerSelectors } from '.';

@Injectable()
export class TrackerEffects {
  public saveVideoUsage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.saveVideoUsage),
      switchMap(() =>
        this.store.select(TrackerSelectors.selectVideoUsage).pipe(take(1))
      ),
      switchMap((videoUsageInput) => {
        if (videoUsageInput.endType === EndType.CLOSE_BROWSER) {
          localStorage.setItem(
            STORAGE_KEY.VIDEO_USAGE,
            JSON.stringify(videoUsageInput)
          );
        }
        return this.trackerService.saveVideoUsave(videoUsageInput);
      }),
      map((videoUsage) => {
        if (videoUsage.endtype === EndType.CLOSE_BROWSER) {
          localStorage.removeItem(STORAGE_KEY.VIDEO_USAGE);
        }
        return TrackerActions.saveVideoUsageSuccess();
      })
    );
  });

  public saveLocalVideoUsage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.saveLocalVideoUsage),
      switchMap(() => {
        const videoUsageStr = localStorage.getItem(STORAGE_KEY.VIDEO_USAGE);
        const videoUsage: VideoUsageInput = videoUsageStr
          ? JSON.parse(videoUsageStr)
          : null;

        if (videoUsage) {
          return this.trackerService.saveVideoUsave(videoUsage);
        }

        return EMPTY;
      }),
      map(() => {
        localStorage.removeItem(STORAGE_KEY.VIDEO_USAGE);
        return TrackerActions.saveLocalVideoUsageSuccess();
      })
    );
  });

  public reportDownloaded$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.reportDownloaded),
      switchMap(() => this.trackerService.reportDownloaded()),
      map(() => {
        this.goalFacade.setReportDownloaded(true);
        return TrackerActions.reportDownloadedSuccess();
      })
    );
  });

  public reportShared$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.reportShared),
      switchMap(() => this.trackerService.reportShared()),
      map(() => {
        this.goalFacade.setReportShared(true);
        return TrackerActions.reportSharedSuccess();
      })
    );
  });

  public startInterestsTracking$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.startInterestsTracking),
      switchMap(() => this.trackerService.startInterestsTracking()),
      map((tracker) => {
        return TrackerActions.startInterestsTrackingSuccess({ tracker });
      })
    );
  });

  public savePathwayPageUsage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.savePathwayPageUsage),
      switchMap(({ input }) => this.trackerService.savePathwayPageUsage(input)),
      map((success) => {
        return TrackerActions.savePathwayPageUsageSuccess({ success });
      })
    );
  });

  constructor(
    private actions$: Actions,
    private trackerService: TrackerService,
    private store: Store<AppState>,
    private goalFacade: GoalFacade
  ) {}
}
